import { useState, useCallback, useRef, useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useCluster } from '../hooks/useCluster';
import { useFeatureGates } from '../hooks/useFeatureGates';
import { useTranslation } from '../i18n';
import { LocaleToggle } from '../components/LocaleToggle';
import { ENTERPRISE_EDITION_GATE, setEnterpriseEditionEnabled } from '../features/enterpriseEdition';

/* ---------- Types ---------- */

export interface ShellUser {
  name: string;
  role: 'platform-admin' | 'tenant-admin';
  onLogout: () => void;
}

interface ShellProps {
  readonly user: ShellUser;
}

/* ---------- Navigation Config ---------- */

type IconName =
  | 'grid'
  | 'building'
  | 'layers'
  | 'bars'
  | 'database'
  | 'shield'
  | 'pulse'
  | 'doc'
  | 'gear'
  | 'lines'
  | 'people'
  | 'lock'
  | 'play'
  | 'apps';

/**
 * Line icons, drawn rather than typed: an emoji renders in the system emoji
 * font, which ignores the rail's colour and weight and looks different on every
 * platform. These inherit currentColor, so the active state tints them.
 */
const ICONS: Record<IconName, React.JSX.Element> = {
  grid: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></>,
  building: <><path d="M3 21V7l6-4 6 4v14" /><path d="M15 21V11l6 3v7" /></>,
  layers: <><path d="M12 3 3 7.5 12 12l9-4.5L12 3Z" /><path d="M3 16.5 12 21l9-4.5" /><path d="M3 12l9 4.5L21 12" /></>,
  bars: <><path d="M4 20V10" /><path d="M10 20V4" /><path d="M16 20v-7" /><path d="M22 20H2" /></>,
  database: <><ellipse cx="12" cy="6" rx="8" ry="3" /><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" /><path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></>,
  shield: <path d="M12 3l8 3v6c0 5-3.4 8.2-8 9-4.6-.8-8-4-8-9V6l8-3Z" />,
  pulse: <path d="M3 12h4l3 8 4-16 3 8h4" />,
  doc: <><path d="M5 3h9l5 5v13H5z" /><path d="M14 3v5h5" /><path d="M9 13h6" /><path d="M9 17h6" /></>,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1" /></>,
  lines: <><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h10" /></>,
  people: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5" /><path d="M16 5.5a3 3 0 0 1 0 5.7" /><path d="M18 15c2 .7 3 2.2 3 5" /></>,
  lock: <><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
  play: <><circle cx="12" cy="12" r="9" /><path d="M10 8.5l6 3.5-6 3.5z" /></>,
  apps: <><rect x="6" y="2" width="12" height="20" rx="2" /><path d="M11 18h2" /></>,
};

function NavIcon({ name }: { readonly name: IconName }) {
  return (
    <span className="nav-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">{ICONS[name]}</svg>
    </span>
  );
}

/** `label` is a translation key, resolved at render so the switch repaints it. */
interface NavItemConfig {
  label: string;
  to: string;
  icon: IconName;
}

interface NavSection {
  label: string;
  items: NavItemConfig[];
}

const clusterAdminSections: NavSection[] = [
  {
    label: 'nav.section.platform',
    items: [
      { label: 'nav.dashboard', to: '/admin/dashboard', icon: 'grid' },
      { label: 'nav.tenants', to: '/admin/tenants', icon: 'building' },
      { label: 'nav.components', to: '/admin/components', icon: 'layers' },
      { label: 'nav.upgrades', to: '/admin/upgrades', icon: 'bars' },
      { label: 'nav.backupDr', to: '/admin/backup', icon: 'database' },
    ],
  },
  {
    label: 'nav.section.governance',
    items: [
      { label: 'nav.platformPolicy', to: '/admin/policy', icon: 'shield' },
      { label: 'nav.monitoring', to: '/admin/monitoring', icon: 'pulse' },
      { label: 'nav.auditLog', to: '/admin/audit', icon: 'doc' },
    ],
  },
  {
    label: 'nav.section.system',
    items: [
      { label: 'nav.operator', to: '/admin/operator', icon: 'gear' },
      { label: 'nav.settings', to: '/admin/settings', icon: 'lines' },
    ],
  },
];

const tenantAdminSections: NavSection[] = [
  {
    label: 'nav.section.tenant',
    items: [
      { label: 'nav.overview', to: '/tenant/overview', icon: 'grid' },
      { label: 'nav.deployments', to: '/tenant/deployments', icon: 'play' },
      { label: 'nav.apps', to: '/tenant/apps', icon: 'apps' },
      { label: 'nav.users', to: '/tenant/users', icon: 'people' },
    ],
  },
  {
    label: 'nav.section.configuration',
    items: [
      { label: 'nav.config', to: '/tenant/config', icon: 'lines' },
      { label: 'nav.iam', to: '/tenant/iam', icon: 'lock' },
      { label: 'nav.backup', to: '/tenant/backup', icon: 'database' },
      { label: 'nav.audit', to: '/tenant/audit', icon: 'doc' },
    ],
  },
];

function clusterDotColorFor(phase: string): string {
  if (phase === 'Ready') return 'green';
  if (phase === 'Degraded') return 'red';
  return 'amber';
}

/* ---------- Shell Component ---------- */

export function Shell({ user }: ShellProps) {
  const { user: authUser } = useAuth();
  const { t } = useTranslation();
  const cluster = useCluster();
  const gates = useFeatureGates();
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
    }
    return 'light';
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      document.documentElement.dataset.theme = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const gate = gates.data?.gates.find((g) => g.name === ENTERPRISE_EDITION_GATE);
    setEnterpriseEditionEnabled(gate?.enabled ?? false);
  }, [gates.data]);

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  const isClusterAdmin = user.role === 'platform-admin';
  const sections = isClusterAdmin ? clusterAdminSections : tenantAdminSections;

  const displayName = authUser?.username ?? user.name;
  const email = authUser?.email ?? '';
  const initials = displayName
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

  // Real data from cluster CR, or sensible defaults
  const clusterName = cluster.data?.name ?? '';
  const clusterPhase = cluster.data?.phase ?? '';
  const clusterVersion = cluster.data?.version ? `v${cluster.data.version}` : '';
  const installedVersion = cluster.data?.installedVersion ? `v${cluster.data.installedVersion}` : '';
  const clusterDomain = cluster.data?.domain ?? '';
  const clusterDotColor = clusterDotColorFor(clusterPhase);

  return (
    <div className="shell">
      {/* Off-screen until focused; jumps a keyboard/screen-reader user past
          the sovereign bar, topbar and sidebar straight to the page content. */}
      <a href="#main-content" className="skip-link">
        {t('nav.skipToContent')}
      </a>

      {/* Sovereign Bar — real data from cluster CR.
          A second `<header>` landmark alongside the topbar below, so it
          carries its own accessible name (WAI-ARIA: multiple banners must
          be individually labeled). */}
      <header className="sov-bar" aria-label={t('nav.sovereigntyStatus')}>
        <div className="sov-seg">
          <div className={`sov-dot ${clusterDotColor}`} />
          <span>{clusterName}</span>
        </div>
        {clusterVersion && (
          <div className="sov-seg" style={{ paddingLeft: 10 }}>
            <span className="sov-version">{clusterVersion}</span>
          </div>
        )}
        {cluster.data?.complianceProfile && cluster.data.complianceProfile !== 'none' && (
          <div className="sov-seg">
            <span className="sov-badge">{cluster.data.complianceProfile.toUpperCase()}</span>
          </div>
        )}
        <div className="sov-right">
          <span className="sov-badge">{isClusterAdmin ? 'cluster-admin' : 'tenant-admin'}</span>
        </div>
      </header>

      {/* Topbar */}
      <header className="topbar">
        <div className="logo-wrap">
          <div className="logo-text">openDesk <span>Operator</span></div>
        </div>

        <dl className="topbar-facts">
          {clusterName && (
            <div className="topbar-fact">
              <dt>{t('nav.cluster')}</dt>
              <dd className="mono">{clusterName}</dd>
            </div>
          )}
          {clusterDomain && (
            <div className="topbar-fact">
              <dt>{t('nav.domain')}</dt>
              <dd className="mono">{clusterDomain}</dd>
            </div>
          )}
          {clusterPhase && (
            <div className="topbar-fact">
              <dt>{t('nav.phase')}</dt>
              <dd>{clusterPhase}</dd>
            </div>
          )}
        </dl>

        <div className="topbar-right">
          <LocaleToggle />

          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? t('common.theme.light') : t('common.theme.dark')}
            title={t('common.theme.toggle')}
            type="button"
          >
            {theme === 'dark' ? '\uD83C\uDF19' : '\u2600\uFE0F'}
          </button>

          {/* User Menu */}
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <button
              className="avatar"
              onClick={() => setUserMenuOpen((p) => !p)}
              aria-label={`${t('nav.userMenu')}: ${displayName}`}
              aria-expanded={userMenuOpen}
              type="button"
            >
              {initials}
            </button>
            {userMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 6,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--rl)',
                  boxShadow: 'var(--sh-l)',
                  minWidth: 200,
                  zIndex: 100,
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ font: '600 13px var(--fd)', color: 'var(--t)' }}>{displayName}</div>
                  {email && <div style={{ font: '400 11px var(--fb)', color: 'var(--t3)', marginTop: 2 }}>{email}</div>}
                  <div style={{ font: '500 10px var(--fm)', color: 'var(--t4)', marginTop: 4 }}>{user.role}</div>
                </div>
                <button
                  type="button"
                  onClick={() => { setUserMenuOpen(false); user.onLogout(); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '10px 14px',
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    font: '500 12px var(--fb)',
                    color: 'var(--red)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {t('auth.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Area */}
      <div className="main-area">
        {/* Sidebar */}
        <nav className="sidebar" aria-label={t('nav.main')}>
          <div className="rail-brand">
            <div className="rail-brand-mark">
              <span className="rail-brand-dot" aria-hidden="true" />
              <span className="rail-brand-name">openDesk</span>
            </div>
            <span className="rail-brand-sub">Operator</span>
          </div>

          {sections.map((section) => (
            <div className="sidebar-section" key={section.label}>
              <div className="sidebar-label">{t(section.label)}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                >
                  <NavIcon name={item.icon} />
                  <span className="nav-label">{t(item.label)}</span>
                </NavLink>
              ))}
            </div>
          ))}

          <div className="rail-foot">
            {clusterVersion && (
              <span>
                openDesk <code>{clusterVersion}</code>
              </span>
            )}
            {installedVersion && installedVersion !== clusterVersion && (
              <span>
                {t('nav.installed')} <code>{installedVersion}</code>
              </span>
            )}
          </div>
        </nav>

        {/* Content */}
        <main id="main-content" className="content" tabIndex={-1}>
          <div className="view">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
